import { useMemo } from 'react';

import { useAppSelector } from '@/app/store/hooks';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_BEFORE_MS } from '@/config/sessionConfig';

import type { FrontendConfigItem } from './useFrontendCacheConfig';

/**
 * useSessionConfig
 *
 * Reads idle timeout and warning period from the backend-supplied
 * `cache.frontend` list (keys `application.idle.timeout` and
 * `application.idle.warning`, both in **seconds**) and converts them to
 * milliseconds.
 *
 * Falls back to the env-var / build-time constants from sessionConfig.ts when
 * the cache is not yet populated or the keys are absent.
 */
export function useSessionConfig() {
    const frontend = useAppSelector((state) => state.cache.frontend) as FrontendConfigItem[];

    return useMemo(() => {
        const index: Record<string, string> = {};
        if (frontend?.length) {
            for (const item of frontend) {
                if (item?.cd) index[item.cd] = item.dsc;
            }
        }

        const timeoutMs = index['application.idle.timeout']
            ? Number(index['application.idle.timeout']) * 1000
            : IDLE_TIMEOUT_MS;

        const warningMs = index['application.idle.warning']
            ? Number(index['application.idle.warning']) * 1000
            : IDLE_WARNING_BEFORE_MS;

        return { timeoutMs, warningMs };
    }, [frontend]);
}
